import {createStyles} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {AgentApprovalResponse} from 'graphql-client/__generated/graphqlGlobalTypes';
import AskUserCard from 'llm/chat-library/ask-user/AskUserCard';
import {AskUserReply} from 'llm/chat-library/ask-user/AskUserTypes';
import {getAskUserRequest} from 'llm/chat-library/ask-user/AskUserUtils';
import AgentApprovalCard from 'llm/chat-library/messages/AgentApprovalCard';
import {
  ChatAssistantReportMessageContents,
  SaveChatAssistantReplyOverflowButton,
  SaveChatAssistantReportButton,
} from 'llm/chat-library/messages/ChatAssistantMessageMenu';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import LLMFeedbackChatToolbar from 'llm/feedback/LLMFeedbackChatToolbar';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import LLMMarkdownSection, {LLMMarkdownClassNames} from 'llm/markdown/LLMMarkdownSection';
import {assertNever} from 'util/Types';

import {ChatMessageBubble, ChatMessageToolbar} from './ChatMessageBubble';
import {
  ChatAssistantMessageSender,
  MessageCreator,
  resolveAssistantMessageSenderCreator,
} from './ChatMessageSender';
import {ChatMessageSenderContext} from './ChatMessageSenderContext';
import {AssistantMessage, DisplayMessageAssistantResponseType} from './MessageTypes';
import {getDisplayMessageAssistantResponseType, isApprovalRequestMessage} from './MessageUtils';

const useChatAssistantMarkdownStyles = createStyles((theme) => ({
  chip: {
    marginRight: 8,
    padding: '1px 4px 2px',
    lineHeight: 1,
  },
  tableCopyButton: {
    right: 10,
    height: 43,
  },
  // Compound selectors beat global simple-table.less (same specificity as the old .ai-report overrides).
  simpleTableHeader: {
    '&.simpleTable-header': {
      height: 43,
    },
  },
  simpleTableHeaderCell: {
    '&.simpleTable-header-cell': {
      color: theme.colors.gray[7],
      fontSize: 13,
      lineHeight: '16px',
      fontWeight: 600,
      padding: '10px 12px 10px',
    },
  },
  simpleTableRow: {
    '&.simpleTable-row': {
      height: 30,
      '& td.simpleTable-cell': {
        padding: '6px 12px 6px',
      },
    },
  },
}));

export interface ChatAssistantMessageBubbleProps {
  event: AssistantMessage;
  progressData?: ProgressData;
  citationData?: ProgressData;
  /** If true, hides interactive components with side-effects. */
  readonly?: boolean;
  chatSessionId: string;
  /** Whether WRITE_ACTIONS is already granted for the rest of this chat. */
  hasChatScopedWriteApproval: boolean;
  /** Session display name when loaded; used for default save entry / file names. */
  chatSessionDisplayName?: string | null;
  /**
   * For an approval-request message: the user's persisted response answering it, if any. When set,
   * the card shows a static Approval granted/Approval not granted indicator instead of the buttons.
   */
  approvalAnsweredResponse: AgentApprovalResponse | null;
  /** For a message that asks AskUser questions: the user's reply to them, or null while they are open. */
  askUserReply: AskUserReply;
  messageSenderContext?: ChatMessageSenderContext | null;
  /** Chat session creator; used as sender fallback when the routine runs as creator. */
  sessionCreatorFallback?: MessageCreator | null;
}

/** Routine-backed sender row for assistant bubbles (persisted and streaming). */
function getAssistantMessageSender(
  event: AssistantMessage,
  messageSenderContext: ChatMessageSenderContext | null | undefined,
  sessionCreatorFallback: MessageCreator | null | undefined
): React.ReactNode {
  if (messageSenderContext == null) {
    return null;
  }
  // Streaming replies still show sender labels; resolveAssistantMessageSenderCreator uses run-as fallback.
  return (
    <ChatAssistantMessageSender
      creator={resolveAssistantMessageSenderCreator(event, messageSenderContext, sessionCreatorFallback)}
      senderContext={messageSenderContext}
    />
  );
}

function getToolbarActions(
  event: AssistantMessage,
  {
    readonly,
    chatSessionId,
    chatSessionDisplayName,
    markdown,
    onReplyOverflowOpenChange,
  }: {
    readonly: boolean;
    chatSessionId: string;
    chatSessionDisplayName?: string | null;
    markdown: string;
    onReplyOverflowOpenChange: (isOpen: boolean) => void;
  }
): React.ReactNode {
  const responseType = getDisplayMessageAssistantResponseType(event);
  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPORT: {
      const messageText = getMarkdown(event);
      return !readonly ? (
        <SaveChatAssistantReportButton
          markdownReport={messageText}
          chatSessionId={chatSessionId}
          chatSessionDisplayName={chatSessionDisplayName}
          assistantMessage={event}
        />
      ) : null;
    }
    case DisplayMessageAssistantResponseType.REPLY:
      return !readonly ? (
        <SaveChatAssistantReplyOverflowButton
          markdownReport={markdown}
          chatSessionId={chatSessionId}
          chatSessionDisplayName={chatSessionDisplayName}
          assistantMessage={event}
          onOpenChange={onReplyOverflowOpenChange}
        />
      ) : null;
    default:
      assertNever(responseType);
      return null;
  }
}

function getMarkdown(event: AssistantMessage): string {
  return event.messageBlocks
    .flatMap((block) => {
      switch (block.__typename) {
        case 'DisplayTextBlockNode':
          return block.text;
        case 'DisplayFileBlockNode':
          // Ignore file blocks for now. Deep Research returns file blocks when the image generation tool is called.
          // These are rendered inline using bnch_file tags, so we shouldn't render them here.
          return [];
        default:
          assertNever(block);
          return [];
      }
    })
    .join('\n');
}

export default function ChatAssistantMessageBubble({
  event,
  progressData,
  citationData,
  readonly = false,
  chatSessionId,
  hasChatScopedWriteApproval,
  chatSessionDisplayName = null,
  approvalAnsweredResponse,
  askUserReply,
  messageSenderContext = null,
  sessionCreatorFallback = null,
}: ChatAssistantMessageBubbleProps): JSX.Element {
  const [isReplyOverflowOpen, setIsReplyOverflowOpen] = useState(false);
  const {classes} = useChatAssistantMarkdownStyles();
  const askUserRequest = event.__typename === 'DisplayMessageChatEventNode' ? getAskUserRequest(event) : null;

  // A write-approval request or AskUser questions render like a normal assistant reply, with a
  // distinct system card appended inside the message border. Derived directly from the event that
  // carries the request, so callers don't need to special-case the message type. Once answered, the
  // card shows a static summary instead of its actions.
  let footer: React.ReactNode = null;
  if (event.__typename === 'DisplayMessageChatEventNode' && isApprovalRequestMessage(event)) {
    footer = (
      <AgentApprovalCard
        category={event.agentApprovalRequest.category}
        chatSessionId={chatSessionId}
        requestEventId={event.id}
        answeredResponse={approvalAnsweredResponse}
        readonly={readonly}
        hasChatScopedWriteApproval={hasChatScopedWriteApproval}
      />
    );
  } else if (event.__typename === 'DisplayMessageChatEventNode' && askUserRequest != null) {
    footer = (
      <AskUserCard
        questions={askUserRequest.questions}
        requestEventId={event.id}
        reply={askUserReply}
        chatSessionId={chatSessionId}
        readonly={readonly}
      />
    );
  }

  const markdownClassNames: LLMMarkdownClassNames = useMemo(
    () => ({
      chip: classes.chip,
      tableCopyButton: classes.tableCopyButton,
      simpleTableHeader: classes.simpleTableHeader,
      simpleTableHeaderCell: classes.simpleTableHeaderCell,
      simpleTableRow: classes.simpleTableRow,
    }),
    [classes]
  );

  // Implicitly, message blocks are markdown. In the future, that might not be the case, and the block
  // itself will indicate its type.
  const markdown = useMemo(() => getMarkdown(event), [event]);
  const responseType = getDisplayMessageAssistantResponseType(event);

  const toolbar = useMemo(
    () => (
      <ChatMessageToolbar
        left={
          // Only render the feedback toolbar for persisted messages.
          !readonly && event.__typename === 'DisplayMessageChatEventNode' ? (
            <LLMFeedbackChatToolbar
              chatEventId={event.id}
              chatEventCreatedAt={event.createdAt}
              agentType={event.agentType}
            />
          ) : (
            <div>
              {event.__typename === 'ClientStreamingAssistantMessage' && event.progressMessage ? (
                <LLMLoadingMessage text={event.progressMessage} />
              ) : null}
            </div>
          )
        }
        right={getToolbarActions(event, {
          readonly,
          chatSessionId,
          chatSessionDisplayName,
          markdown,
          onReplyOverflowOpenChange: setIsReplyOverflowOpen,
        })}
        hideRightUntilHover={responseType === DisplayMessageAssistantResponseType.REPLY}
        showRightAction={responseType !== DisplayMessageAssistantResponseType.REPLY || isReplyOverflowOpen}
      />
    ),
    [event, readonly, chatSessionId, chatSessionDisplayName, markdown, responseType, isReplyOverflowOpen]
  );

  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPLY:
      return (
        <ChatMessageBubble
          sender={getAssistantMessageSender(event, messageSenderContext, sessionCreatorFallback)}
          variant="reply"
          toolbar={toolbar}
          footer={footer}
        >
          <LLMMarkdownSection
            markdown={markdown}
            chatSessionId={chatSessionId}
            classNames={markdownClassNames}
            canSaveChartsToEntry={!readonly}
          />
        </ChatMessageBubble>
      );
    case DisplayMessageAssistantResponseType.REPORT:
      return (
        <ChatMessageBubble
          sender={getAssistantMessageSender(event, messageSenderContext, sessionCreatorFallback)}
          variant="report"
          toolbar={toolbar}
        >
          <ChatAssistantReportMessageContents
            report={markdown}
            progressData={progressData}
            citationData={citationData}
            readonly={readonly}
            chatSessionId={chatSessionId}
            markdownClassNames={markdownClassNames}
          />
        </ChatMessageBubble>
      );
    default:
      assertNever(responseType);
      // NB: This should never be exercised as responseType is generated on the client.
      // If we move this to be a persisted field, this fallback may be exercised.
      return (
        <ChatMessageBubble
          sender={getAssistantMessageSender(event, messageSenderContext, sessionCreatorFallback)}
          variant="reply"
          toolbar={toolbar}
          footer={footer}
        >
          <LLMMarkdownSection
            markdown={markdown}
            chatSessionId={chatSessionId}
            classNames={markdownClassNames}
            canSaveChartsToEntry={!readonly}
          />
        </ChatMessageBubble>
      );
  }
}
